import { createElement, Fragment } from "react";
import type { ComponentConfig } from "@measured/puck";
import { StyleWrapper } from "@/components/cms-puck/layout/StyleWrapper";
import { DEFAULT_BLOCK_STYLE, type BlockStyle } from "@/lib/cms-puck/block-style";

/**
 * withStyle — helper de CONFIG (édite chaque bloc UNE seule fois). Universalise
 * la sidebar « Style » : pour un `ComponentConfig` existant, il
 *  1. injecte le champ `style` (type:'custom') APRÈS les fields existants. Son
 *     `render` est VIDE : l'édition du style est désormais pilotée par le panneau
 *     à 3 onglets (Contenu | Design | Avancé, cf. `FieldsPanel.tsx`) via
 *     l'override Puck `overrides.fields`. On conserve néanmoins le champ dans la
 *     config pour que `style?: BlockStyle` reste typé et que `DEFAULT_BLOCK_STYLE`
 *     soit semé dans les props (forme de données INCHANGÉE) ;
 *  2. ajoute `style: DEFAULT_BLOCK_STYLE` aux defaultProps ;
 *  3. selon `mode` :
 *     - `"wrapper"` (défaut, blocs SECTION/layout) : enveloppe le `render` dans
 *       `<StyleWrapper style={props.style}>` → le style (fond inclus) va sur un
 *       conteneur pleine largeur. Correct pour Container/Hero/Media/texte… ;
 *     - `"self"` (widgets ATOMIQUES : Bouton, Icône) : NE wrappe PAS — le render
 *       du bloc applique lui-même le style résolu SUR son élément (via
 *       `useBlockStyleCss`). Indispensable quand l'élément a déjà un fond de
 *       variante (ex. `<span>` du bouton) que seul un style inline peut battre.
 *
 * Appliqué à tous les blocs via `applyStyleToAll` dans config.tsx. Le typage par
 * bloc des props (avec `style?: BlockStyle`) est réaffirmé là-bas ; ici on opère
 * sur un `ComponentConfig` générique pour ne pas se battre avec les contraintes
 * « Exact » des génériques Puck.
 */
type AnyComponentConfig = ComponentConfig<Record<string, unknown>>;

export type StyleMode = "wrapper" | "self";

export type WithStyleOptions = {
  /** Classes additionnelles sur le StyleWrapper (structurel, hors onglet Design). */
  wrapperClassName?:
    | string
    | ((props: Record<string, unknown>) => string | undefined);
  /**
   * Repli largeur max du contenu interne (ex. `Container.maxWidth` historique)
   * quand `style.layout.contentMaxWidth` n'est pas défini.
   */
  contentMaxWidthFallback?: (
    props: Record<string, unknown>,
  ) => import("@/lib/cms-puck/layout").LayoutMaxWidth | null | undefined;
  /**
   * Style effectivement rendu, dérivé des props (ex. repli mobile des Colonnes
   * empilées). Le panneau d'édition garde le style stocké tel quel.
   */
  renderStyle?: (props: Record<string, unknown>) => BlockStyle | undefined;
};

export function withStyle(
  component: AnyComponentConfig,
  mode: StyleMode = "wrapper",
  options?: WithStyleOptions,
): AnyComponentConfig {
  const oldRender = component.render;
  const resolveWrapperClassName = (props: Record<string, unknown>) => {
    const wc = options?.wrapperClassName;
    if (!wc) return undefined;
    return typeof wc === "function" ? wc(props) : wc;
  };
  const resolveContentMaxWidthFallback = (props: Record<string, unknown>) => {
    const fb = options?.contentMaxWidthFallback;
    if (!fb) return undefined;
    return fb(props) ?? undefined;
  };
  return {
    ...component,
    fields: {
      ...(component.fields ?? {}),
      style: {
        type: "custom",
        label: "Style",
        // Rendu vide : l'édition passe par le panneau à 3 onglets (FieldsPanel),
        // pas par le champ natif. Le champ existe juste pour le typage + defaults.
        render: () => createElement(Fragment, null),
      },
    },
    defaultProps: {
      ...(component.defaultProps ?? {}),
      style: DEFAULT_BLOCK_STYLE,
    },
    // Mode "self" : le bloc applique le style sur son propre élément → on rend le
    // render d'origine tel quel (pas de wrapper pleine largeur).
    render:
      mode === "self"
        ? oldRender
        : (props) =>
            createElement(StyleWrapper, {
              style: options?.renderStyle
                ? options.renderStyle(props)
                : (props.style as BlockStyle | undefined),
              className: resolveWrapperClassName(props),
              contentMaxWidthFallback: resolveContentMaxWidthFallback(props),
              children: oldRender ? oldRender(props) : null,
            }),
  } as AnyComponentConfig;
}
